import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { fetchCihazSorgulama } from '../services/cihazSorgulamaService';
import { parseApiDate } from '../utils/security';
import './CihazSorgulamaPanel.css';

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const formatTarih = (val) => {
  const d = parseApiDate(val);
  if (!d || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getGarantiDurum = (cihaz) => {
  if (!cihaz?.garantiBitisTarihi) return 'belirsiz';
  const bitis = parseApiDate(cihaz.garantiBitisTarihi);
  if (!bitis || Number.isNaN(bitis.getTime())) return 'belirsiz';
  const bugun = new Date();
  bugun.setHours(0, 0, 0, 0);
  bitis.setHours(0, 0, 0, 0);
  return bitis >= bugun ? 'aktif' : 'bitmis';
};

const GarantiPill = ({ cihaz, tarih }) => {
  const durum = getGarantiDurum(cihaz);
  return (
    <span className={`cihaz-sorgulama-garanti-pill cihaz-sorgulama-garanti-pill--${durum}`}>
      {tarih}
    </span>
  );
};

/**
 * @param {{ buildServisKaydiLink: (servisKaydiId: string) => string, showIlRestrictionHint?: boolean }} props
 */
const CihazSorgulamaPanel = ({ buildServisKaydiLink, showIlRestrictionHint = false }) => {
  const [seriInput, setSeriInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [cihazlar, setCihazlar] = useState([]);

  const handleSearch = useCallback(async () => {
    const q = seriInput.trim();
    if (!q) {
      setError('Seri numarası girin');
      setSearched(true);
      setCihazlar([]);
      return;
    }
    setLoading(true);
    setError('');
    setSearched(true);
    try {
      const res = await fetchCihazSorgulama({ seriNumarasi: q });
      setCihazlar(res?.veri?.cihazlar || []);
    } catch (err) {
      setCihazlar([]);
      setError(getErrorMessage(err, 'Arama sonucu bulunamadı'));
    } finally {
      setLoading(false);
    }
  }, [seriInput]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <>
      <div className="cihaz-sorgulama-toolbar">
        <div className="search-box">
          <i className="fas fa-search search-icon" aria-hidden />
          <input
            type="text"
            className="search-input"
            placeholder="Seri numarası"
            value={seriInput}
            onChange={(e) => setSeriInput(e.target.value)}
            onKeyDown={onKeyDown}
            autoComplete="off"
          />
        </div>
        <button type="button" className="btn btn-primary" onClick={handleSearch} disabled={loading}>
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} />
              Sorgulanıyor...
            </>
          ) : (
            <>
              <i className="fas fa-search" style={{ marginRight: 8 }} />
              Sorgula
            </>
          )}
        </button>
      </div>

      {showIlRestrictionHint ? (
        <p className="cihaz-sorgulama-result-meta" style={{ marginBottom: 16 }}>
          Yalnızca teknik servisinizin bulunduğu ildeki müşterilerin cihazları listelenir.
        </p>
      ) : null}

      {error ? <div className="cihaz-sorgulama-error">{error}</div> : null}

      {!loading && searched && !error && cihazlar.length === 0 ? (
        <div className="cihaz-sorgulama-empty">Arama sonucu bulunamadı</div>
      ) : null}

      {cihazlar.map((cihaz) => {
        const musteriAd = `${cihaz.musteri?.ad || ''} ${cihaz.musteri?.soyad || ''}`.trim() || '—';
        const adres = cihaz.varsayilanAdres;
        const adresMetin = adres
          ? [adres.adres, adres.ilceAd, adres.ilAd].filter(Boolean).join(', ')
          : null;

        return (
          <article key={cihaz.id} className="cihaz-sorgulama-result">
            <div className="cihaz-sorgulama-result-head">
              <h3>SN: {cihaz.seriNumarasi || '—'}</h3>
              <div className="cihaz-sorgulama-result-meta">
                <div>
                  <strong>Ürün:</strong> {cihaz.urunAdi || '—'}
                  {cihaz.urunKodu ? ` (${cihaz.urunKodu})` : ''}
                </div>
                <div>
                  <strong>Garanti Başlangıç:</strong>{' '}
                  <GarantiPill
                    cihaz={cihaz}
                    tarih={formatTarih(cihaz.garantiBaslangicTarihi)}
                  />
                </div>
                <div>
                  <strong>Garanti Bitiş Tarihi:</strong>{' '}
                  <GarantiPill
                    cihaz={cihaz}
                    tarih={formatTarih(cihaz.garantiBitisTarihi)}
                  />
                </div>
              </div>
            </div>

            <div className="cihaz-sorgulama-musteri">
              <div className="cihaz-sorgulama-musteri-title">Müşteri</div>
              <div className="cihaz-sorgulama-musteri-body">
                <div>{musteriAd}</div>
                {cihaz.musteri?.telefon ? <div>{cihaz.musteri.telefon}</div> : null}
                {adresMetin ? <div style={{ marginTop: 6 }}>{adresMetin}</div> : null}
                {!adres ? (
                  <div className="cihaz-sorgulama-warn">Varsayılan adres tanımlı değil</div>
                ) : null}
              </div>
            </div>

            <div className="cihaz-sorgulama-sk-section">
              <div className="cihaz-sorgulama-sk-title">Servis kayıtları</div>
              {!cihaz.servisKayitlari?.length ? (
                <div className="cihaz-sorgulama-sk-empty">Bu cihaz için servis kaydı bulunamadı</div>
              ) : (
                <table className="cihaz-sorgulama-sk-table">
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>Tarih</th>
                      <th>İşlem tipi</th>
                      <th>Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cihaz.servisKayitlari.map((sk) => (
                      <tr key={sk.id} className="cihaz-sorgulama-sk-row">
                        <td>
                          <Link
                            to={buildServisKaydiLink(sk.id)}
                            className="cihaz-sorgulama-sk-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {sk.numara || sk.id}
                          </Link>
                        </td>
                        <td>{formatTarih(sk.olusturmaTarihi)}</td>
                        <td>{sk.islemTipi || '—'}</td>
                        <td>{sk.durum || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </article>
        );
      })}
    </>
  );
};

export default CihazSorgulamaPanel;
